/**
 * Dashboard section groups for the DISC-AC-133 endpoint split (W-R4-2).
 *
 * One group == one additive endpoint; the monolith `reports/dashboard` is
 * "ALL groups" so its query order, payload and golden pins stay byte-stable.
 * The section-conditional core (`DashboardReportQuery`) consults the group set
 * to decide which of its guarded sub-queries run; the controller projects the
 * result to the group's payload keys ("each endpoint returns ONLY its own
 * section").
 *
 * Section→group mapping decisions (recorded in the wave evidence):
 *   - `anomalies` + `rootCauseDrivers` ride `acquisition`: the anomaly
 *     paid-exposure decoration is computed from the campaign-economics rows,
 *     which that endpoint's mega-query already produces — serving them from
 *     `funnel` would either re-run the mega-query or silently degrade the
 *     exposure to `no_paid_data`.
 *   - `paymentOutcomes` + `moneyDiagnosis` ride `summary` (the money home):
 *     the payments query already feeds the header KPI derivation.
 *   - `artifacts` (viz_artifact_performance screen rollups) ride `funnel` —
 *     mart-backed screen material (SD-2).
 *   - the former `creatives` group died at W-R6 (DISC-AC-149/150): the
 *     `reports/creatives` endpoint serves each view from its OWN lane
 *     (performance SQL / linkage+jff reader) and no dashboard payload carries
 *     creative sections any more.
 */

export const DASHBOARD_SECTION_GROUPS = [
  'summary',
  'funnel',
  'acquisition',
  'data-health',
] as const;

export type DashboardSectionGroup = (typeof DASHBOARD_SECTION_GROUPS)[number];

/** Payload keys each split endpoint serves (projection lists for the controller). */
export const SECTION_GROUP_PAYLOAD_KEYS: Record<DashboardSectionGroup, readonly string[]> = {
  summary: [
    'summary', 'priorSummary', 'priorWindow', 'paymentOutcomes', 'moneyDiagnosis',
    // money value-basis contracts (controller-attached constants)
    'realizedValueBasis', 'realizedGrossBasis', 'netCollectedBasis', 'settlementBasis',
    'projectedValueBasis', 'projectedGrossBasis', 'projectedNetCollectedBasis',
    'projectedSettlementBasis', 'projectedContributionBasis', 'projectedValueBases',
    'contributionBasis', 'ltvNetOfRefunds',
  ],
  funnel: [
    // R-wave (FT-AC-105): `personScopeRollup` rides with the placement rows —
    // same lane, same 'active-placements' section, same degrade. Omitting it
    // here would strip the key on every SPLIT endpoint while the monolith kept
    // serving it, so the hub headline would silently stay n/a in production.
    'aliasSummaries', 'aliasOnboardingSummaries', 'personScopeRollup', 'funnelHealthDaily',
    // People-attribution S5: the versions-table scope block rides the funnel lane.
    'versionPeopleScope',
    'metricTimeseries', 'screenFlowEdges', 'artifacts',
  ],
  acquisition: [
    // F3 (FT-AC-088): the attribution identity block of the paid lane.
    'campaignEconomics', 'campaignEconomicsCoverage', 'campaignEconomicsAttribution', 'moneyRootCause',
    // People-attribution S2: the scope-level de-duplicated people block. An
    // unlisted key is stripped by the SPLIT endpoints in production while
    // the monolith keeps serving it (the R-wave trap) — list every new key.
    'campaignEconomicsPeople',
    // People-attribution S9-C (§16.3): the served unmatched-spend block.
    'campaignEconomicsUnmatched',
    // W-R5 (DISC-AC-144): server-computed Paid Acquisition header tiles.
    'paidSummaryTiles',
    'anomalies', 'rootCauseDrivers', 'verdictThresholds',
    'realizedValueBasis', 'realizedGrossBasis', 'netCollectedBasis', 'settlementBasis',
    'projectedValueBasis', 'projectedGrossBasis', 'projectedNetCollectedBasis',
    'projectedSettlementBasis', 'projectedContributionBasis', 'projectedValueBases',
    'contributionBasis', 'ltvNetOfRefunds',
  ],
  'data-health': [
    'dataQuality',
  ],
} as const;

/**
 * PE-1 section ids owned by each group — sectionErrors entries are filtered to
 * the endpoint's own sections (the monolith keeps the full list).
 */
export const SECTION_GROUP_ERROR_SECTIONS: Record<DashboardSectionGroup, readonly string[]> = {
  summary: ['funnel-health', 'metric-timeseries', 'payments'],
  funnel: ['active-placements', 'funnel-health', 'metric-timeseries', 'screen-flow', 'artifacts'],
  acquisition: ['anomaly-inbox', 'root-cause', 'paid-acquisition'],
  'data-health': ['data-quality'],
} as const;
